import axios from 'axios'
import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  Button,
  Header,
  LoadingSpinner,
  SignaturePad,
  SignaturePadHandle,
} from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, EASYSIGN_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { rootStore } from '@/store/Root.store'
import { cx } from '@/utils/cx'
import './EasySignHandwrittenSignaturePage.css'

const MAX_SIGNATURE_BYTES = 2 * 1024 * 1024

const estimateDecodedBytes = (dataUrl: string): number => {
  const commaIndex = dataUrl.indexOf(',')
  if (commaIndex === -1) return 0

  const base64Length = dataUrl.length - commaIndex - 1
  if (base64Length <= 0) return 0

  const padding =
    dataUrl[dataUrl.length - 1] !== '=' ? 0 : dataUrl[dataUrl.length - 2] === '=' ? 2 : 1

  return Math.floor((base64Length * 3) / 4) - padding
}

export const EasySignHandwrittenSignaturePage = observer(() => {
  const signaturePadRef = useRef<SignaturePadHandle>(null)

  const store = useContext(StoreContext)
  const { easySignHandwrittenSignaturePage: trans } = store.TranslationsState.translations
  const { screenSize, isMobile } = store.AppState

  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  const [btnLoading, setBtnLoading] = useState(false)
  const [isEmpty, setIsEmpty] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const goBackHandler = () => {
    rootStore.ScenarioState.prevPage()
  }

  const handleClear = () => {
    signaturePadRef.current?.clear()
    setError(null)
  }

  const handleProceed = async () => {
    if (btnLoading || isEmpty) return

    const handwrittenSignatureImage = signaturePadRef.current?.toDataURL()
    if (!handwrittenSignatureImage) return

    if (estimateDecodedBytes(handwrittenSignatureImage) > MAX_SIGNATURE_BYTES) {
      setError(trans?.errorSignatureTooLarge ?? 'Signature is too large. Please try again.')
      return
    }

    try {
      setError(null)
      setBtnLoading(true)

      await axiosInstance.post(
        `${API_URL}/${EASYSIGN_SERVICE_PATH}/sign-document`,
        { handwrittenSignatureImage },
        {
          withCredentials: true,
        }
      )

      forceRedirect(`${API_URL}/${EASYSIGN_SERVICE_PATH}/complete`)
    } catch (e) {
      devLog(e)
      setError(
        axios.isAxiosError(e) && e.response?.status === 400
          ? (trans?.errorSignatureTooLarge ?? 'Signature is too large. Please try again.')
          : (trans?.errorGeneric ?? 'Something went wrong. Please try again.')
      )
      setBtnLoading(false)
    }
  }

  useEffect(() => {
    if (screenSize.width && screenSize.width <= 980) {
      setMobileContainerHeight(`calc(${window.innerHeight - 250}px)`)
    }
  }, [screenSize.width])

  return (
    <div className="page-container mobile-m-0 mobile-p-140-20-20 handwritten-signature-page">
      <button
        className="generic__back-button generic__back-button--absolute"
        onClick={goBackHandler}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.7205 23.2794C15.0589 23.6178 15.6077 23.6178 15.9461 23.2794C16.2846 22.9409 16.2846 22.3922 15.9461 22.0537L10.759 16.8666L23.3333 16.8666C23.8119 16.8666 24.1999 16.4785 24.1999 15.9999C24.1999 15.5212 23.8119 15.1332 23.3333 15.1332L10.759 15.1332L15.9461 9.94605C16.2846 9.60759 16.2846 9.05885 15.9461 8.72039C15.6077 8.38194 15.0589 8.38194 14.7205 8.72039L8.05383 15.3871C7.71537 15.7255 7.71537 16.2743 8.05383 16.6127L14.7205 23.2794Z"
            fill="black"
          />
        </svg>

        {trans?.back}
      </button>
      <div
        className="layout__content-container-with-scroll"
        style={
          isMobile
            ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
            : undefined
        }
      >
        <Header className="no-margin">{trans?.header}</Header>

        <p className="generic__paragraph-centered">{trans?.drawSignature}</p>

        <div className="generic__label">{trans?.yourSignature}</div>

        <SignaturePad
          ref={signaturePadRef}
          ariaLabel={trans?.yourSignature}
          onChange={(empty) => {
            setIsEmpty(empty)
            if (!empty) setError(null)
          }}
        />

        {error && <p className="generic__error-message">{error}</p>}
      </div>

      <div className="layout__content-container-bottom-fixed layout__content-container-bottom-fixed--gap-16">
        <Button onClick={handleClear} className="button--link" disabled={isEmpty}>
          <span className="generic__button-text">{trans?.clear}</span>
        </Button>
        <Button onClick={handleProceed} disabled={isEmpty || btnLoading} tabIndex={0}>
          {btnLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
          >
            {trans?.proceed}
          </span>
        </Button>
      </div>
    </div>
  )
})
